import React from "react";
import _ from "lodash";
import moment from "moment";
import { AnatomyAPI, NetworkAPI } from "apis";

import LoadingUI from "components/common/LoadingUI";
import DateRangePicker from "components/common/DateRangePicker";
import HighchartWrapper from "components/common/HighchartWrapper";
import SwitchTabs from "components/common/SwitchTabs";
import ShowInTwdCheckbox from "components/common/ShowInTwdCheckbox";
import SimpleTooltip from "components/common/SimpleTooltip";
import DemandTypeColorSetButton from "./DemandTypeColorSetButton";

import {
  DEMAND_TYPE_COLORS,
  DEMAND_TYPE_COLORS_CVD,
  DEMAND_GROUPS_COLORS,
  DEMAND_GROUPS_COLORS_CVD,
  REV_LIFT_COLOR_CVD,
  REV_LIFT_COLOR,
} from "components/inventory-rev/DataHelper";

import { UNFILLED } from "constants/DemandType";
import { getQueryStringToObject } from "helpers/QueryString";

import { FiAperture } from "react-icons/fi";

import {
  TYPE,
  GROUP,
  METRIC,
  VIEW,
  dpGroupList,
  getChartOption,
  getLineChartOption,
  getPieChartOption,
  getBarChartOption,
  getDataParams,
  compareParams,
  parseDemandPortfolioData,
  getDemandTypeByView,
} from "./DataHelper";

const fixedWidthStyle = { width: "1280px", margin: "auto" };

class DemandPortfolioView extends React.Component {
  constructor(props) {
    super(props);

    const end = moment().subtract(1, "day");
    const start = end.clone().subtract(7, "day");

    this.state = {
      isLoading: false,
      errMsg: null,

      selectedType: null,
      startDate: start.format("YYYY-MM-DD"),
      endDate: end.format("YYYY-MM-DD"),

      viewOptions: _.values(VIEW).map((v) => ({ label: v, value: v })),
      selectedView: VIEW.ALL,
      metricOptions: [
        { label: "Revenue", value: METRIC.REVENUE },
        { label: "Impressions", value: METRIC.IMPRESSIONS },
      ],
      selectedMetric: METRIC.REVENUE,
      isColorBlindMode: false,
      isInTwd: false,
      currency: "USD",
      twdRate: 1,

      networkId: null,
      networkInfo: null,
      yieldsetId: null,
      yieldsetName: null,
      gamUnitId: null,
      unitInfo: [],
      dpParam: null,
      trendParam: null,
      data: null,
      dateList: [],
      groupedData: null,
      liftData: null,

      stackedChartOption: null,
      lineChartOption: null,
      pieChartOption: null,
    };

    this.handleDateRangeFilterChanged =
      this.handleDateRangeFilterChanged.bind(this);
    this.handleGetData = this.handleGetData.bind(this);
    this.handleChartData = this.handleChartData.bind(this);
    this.checkGetData = this.checkGetData.bind(this);
  }

  async componentDidMount() {
    const { networkId, yieldsetId, gamUnitId, isInTwd, startDate, endDate } =
      getQueryStringToObject(this.props.location.search);

    this.setState({
      isLoading: true,
    });

    try {
      const { currency, twdConvertRate } =
        await NetworkAPI.getLatestTwdConvertRate({ networkId });

      const networks = await NetworkAPI.getNetworks();

      const networkInfo = _.find(networks, {
        networkId: _.toInteger(networkId),
      });

      let type = null;
      if (networkId && _.isNil(yieldsetId) && _.isNil(gamUnitId)) {
        type = TYPE.NETWORK;
      }

      if (networkId && yieldsetId && _.isNil(gamUnitId)) {
        type = TYPE.YIELD_SET;
      }

      if (networkId && _.isNil(yieldsetId) && !_.isNil(gamUnitId)) {
        type = TYPE.AD_UNITS;
      }

      let yieldsetName = null;
      if (type === TYPE.YIELD_SET) {
        const ysList = await NetworkAPI.getYieldSets({ networkId });
        const ys = _.find(ysList, { yieldSetId: _.toInteger(yieldsetId) });
        if (ys) {
          yieldsetName = ys.name;
        }
      }

      let unitInfo = [];
      if (type === TYPE.AD_UNITS) {
        const unitResult = await NetworkAPI.getUnitsByNetworkId({ networkId });
        unitInfo = gamUnitId
          .split(",")
          .map((id) => {
            return _.find(unitResult, { extGamUnitId: _.trim(id) });
          })
          .filter((d) => !_.isNil(d));
      }

      this.setState(
        {
          selectedType: type,
          networkId: _.toInteger(networkId),
          networkInfo,
          yieldsetId: _.toInteger(yieldsetId),
          yieldsetName,
          gamUnitId,
          unitInfo,
          currency,
          isInTwd: _.isNil(isInTwd) ? this.state.isInTwd : isInTwd === "true",
          startDate: startDate || this.state.startDate,
          endDate: endDate || this.state.endDate,
          twdRate: twdConvertRate,
        },
        () => {
          this.handleGetData();
        }
      );
    } catch (error) {
      this.setState({
        isLoading: false,
        errMsg: `Failed to load data: ${error.toString()}`,
      });
    }
  }

  handleDateRangeFilterChanged({ startDate, endDate }) {
    this.setState({
      startDate: moment(startDate).format("YYYY-MM-DD"),
      endDate: moment(endDate).format("YYYY-MM-DD"),
    });
  }

  async handleGetData() {
    const {
      startDate,
      endDate,
      selectedType,
      unitInfo,
      yieldsetId,
      networkInfo,
    } = this.state;

    try {
      this.setState({ isLoading: true, errMsg: null, data: null });

      if (selectedType === TYPE.YIELD_SET) {
        if (yieldsetId === null || yieldsetId === "") {
          throw new Error("Please select a Yield Set.");
        }
      }

      if (selectedType === TYPE.AD_UNITS) {
        const list = unitInfo.map((unit) => unit.extGamUnitId);

        if (list.length === 0) {
          throw new Error("Please provide at least one Ad Unit Ext. GAM ID.");
        }

        if (_.some(list, (v) => !/^\d+$/.test(v))) {
          throw new Error(
            "Invalid Ad Unit Ext. GAM ID format. Please provide valid numeric IDs separated by commas."
          );
        }
      }

      const { dpParam, trendParam } = getDataParams({
        networkId: this.state.networkId,
        yieldsetId: this.state.yieldsetId,
        unitInfo: this.state.unitInfo,
        selectedType: this.state.selectedType,
        startDate: this.state.startDate,
        endDate: this.state.endDate,
      });

      const [trendResult, dpResult] = await Promise.all([
        selectedType === TYPE.AD_UNITS
          ? AnatomyAPI.getUnitRevLiftTrend(trendParam)
          : NetworkAPI.getNetworkSnapshotTrend(trendParam),
        AnatomyAPI.getDemandPortfolio(dpParam),
      ]);

      const { groupedData, liftData, dateList, data } =
        parseDemandPortfolioData({
          trendResult,
          dpResult,
          selectedType,
          startDate,
          endDate,
          networkInfo,
        });

      this.setState(
        {
          isLoading: false,
          data,
          dateList,
          groupedData,
          liftData,
          dpParam,
          trendParam,
        },
        () => {
          this.handleChartData();
        }
      );
    } catch (error) {
      this.setState({
        isLoading: false,
        stackedChartOption: null,
        lineChartOption: null,
        pieChartOption: null,
        errMsg: `Failed to get data: ${error.toString()}`,
      });
    }
  }

  handleChartData() {
    const {
      data,
      dateList,
      currency,
      isColorBlindMode,
      groupedData,
      liftData,
      networkInfo,
      twdRate,
      metricOptions,
    } = this.state;

    const colorSet = isColorBlindMode
      ? DEMAND_TYPE_COLORS_CVD
      : DEMAND_TYPE_COLORS;

    const colorLift = isColorBlindMode ? REV_LIFT_COLOR_CVD : REV_LIFT_COLOR;

    const groupedColorSet = isColorBlindMode
      ? DEMAND_GROUPS_COLORS_CVD
      : DEMAND_GROUPS_COLORS;

    const getParams = (view, metric) => {
      const list =
        view === VIEW.GROUP
          ? dpGroupList
          : getDemandTypeByView({ view, networkInfo });
      return {
        dateList,
        colorSet: view === VIEW.GROUP ? groupedColorSet : colorSet,
        colorLift,
        data: view === VIEW.GROUP ? groupedData : data,
        list:
          metric === METRIC.REVENUE
            ? _.filter(list, (d) => d !== UNFILLED)
            : list,
        liftData,
        twdRate,
        metricOptions,
      };
    };

    const stackedChartOption = _.values(VIEW).reduce((acc, view) => {
      acc[view] = {
        revChartOption: getChartOption({
          ...getParams(view, METRIC.REVENUE),
          metric: METRIC.REVENUE,
          currency,
          isGroup: view === VIEW.GROUP,
        }),
        revTwdChartOption: getChartOption({
          ...getParams(view, METRIC.REVENUE),
          metric: METRIC.REVENUE,
          currency: "TWD",
          isGroup: view === VIEW.GROUP,
        }),
        impChartOption: getChartOption({
          ...getParams(view, METRIC.IMPRESSIONS),
          metric: METRIC.IMPRESSIONS,
          isGroup: view === VIEW.GROUP,
        }),
      };
      return acc;
    }, {});

    const lineChartOption = _.values(VIEW).reduce((acc, view) => {
      acc[view] = {
        revChartOption: ["total", GROUP.BM, GROUP.O].map((group) => {
          return getLineChartOption({
            ...getParams(view, METRIC.REVENUE),
            metric: METRIC.REVENUE,
            currency,
            group,
          });
        }),
        revTwdChartOption: ["total", GROUP.BM, GROUP.O].map((group) => {
          return getLineChartOption({
            ...getParams(view, METRIC.REVENUE),
            metric: METRIC.REVENUE,
            currency: "TWD",
            group,
          });
        }),
        impChartOption: ["total", GROUP.BM, GROUP.O].map((group) => {
          return getLineChartOption({
            ...getParams(view, METRIC.IMPRESSIONS),
            metric: METRIC.IMPRESSIONS,
            group,
          });
        }),
      };
      return acc;
    }, {});

    const pieChartOption = _.values(VIEW).reduce((acc, view) => {
      const isPieChart = view === VIEW.GROUP || view === VIEW.ALL;
      acc[view] = {
        revChartOption: [GROUP.BM, GROUP.O].map((group) => {
          const params = {
            ...getParams(view, METRIC.REVENUE),
            metric: METRIC.REVENUE,
            currency,
            group,
          };
          return isPieChart
            ? getPieChartOption(params)
            : getBarChartOption(params);
        }),
        revTwdChartOption: [GROUP.BM, GROUP.O].map((group) => {
          const params = {
            ...getParams(view, METRIC.REVENUE),
            metric: METRIC.REVENUE,
            currency: "TWD",
            group,
          };
          return isPieChart
            ? getPieChartOption(params)
            : getBarChartOption(params);
        }),
        impChartOption: [GROUP.BM, GROUP.O].map((group) => {
          const params = {
            ...getParams(view, METRIC.IMPRESSIONS),
            metric: METRIC.IMPRESSIONS,
            group,
          };
          return isPieChart
            ? getPieChartOption(params)
            : getBarChartOption(params);
        }),
      };
      return acc;
    }, {});

    this.setState({
      stackedChartOption,
      lineChartOption,
      pieChartOption,
    });
  }

  checkGetData() {
    const { dpParam } = getDataParams({
      networkId: this.state.networkId,
      yieldsetId: this.state.yieldsetId,
      unitInfo: this.state.unitInfo,
      selectedType: this.state.selectedType,
      startDate: this.state.startDate,
      endDate: this.state.endDate,
    });

    return compareParams(this.state.dpParam, dpParam);
  }

  render() {
    const {
      isLoading,
      errMsg,

      networkId,
      networkInfo,
      yieldsetId,
      yieldsetName,
      unitInfo,

      selectedType,
      startDate,
      endDate,
      selectedMetric,
      selectedView,
      isInTwd,
      isColorBlindMode,

      viewOptions,
      metricOptions,

      stackedChartOption,
      lineChartOption,
      pieChartOption,
    } = this.state;

    let parsedChartOption = null;
    if (stackedChartOption) {
      if (selectedMetric === METRIC.REVENUE) {
        parsedChartOption = isInTwd
          ? stackedChartOption[selectedView].revTwdChartOption
          : stackedChartOption[selectedView].revChartOption;
      } else {
        parsedChartOption = stackedChartOption[selectedView].impChartOption;
      }
    }

    let parsedLineChartOption = null;
    if (lineChartOption) {
      if (selectedMetric === METRIC.REVENUE) {
        parsedLineChartOption = isInTwd
          ? lineChartOption[selectedView].revTwdChartOption
          : lineChartOption[selectedView].revChartOption;
      } else {
        parsedLineChartOption = lineChartOption[selectedView].impChartOption;
      }
    }

    let parsedPieChartOption = null;
    if (pieChartOption) {
      if (selectedMetric === METRIC.REVENUE) {
        parsedPieChartOption = isInTwd
          ? pieChartOption[selectedView].revTwdChartOption
          : pieChartOption[selectedView].revChartOption;
      } else {
        parsedPieChartOption = pieChartOption[selectedView].impChartOption;
      }
    }

    return (
      <>
        <div className="border-bottom border flex bg-white">
          <div
            style={fixedWidthStyle}
            className="flex items-center justify-start gap-2 py-2"
          >
            <div className="mr-2 text-base font-bold">
              Demand Type Portfolio
            </div>
          </div>
        </div>
        <div className="min-h-screen bg-white pb-4">
          <div className="relative bg-white">
            {isLoading && (
              <div
                className="absolute left-0 top-0 z-20 flex h-full w-full items-center justify-center"
                style={{ backgroundColor: "rgba(255, 255, 255, 0.8)" }}
              >
                <LoadingUI iconOnly={true}></LoadingUI>
              </div>
            )}
            <div className="bg-white px-2 pb-4 pt-2">
              <div className="flex flex-col" style={fixedWidthStyle}>
                <div className="mb-1 flex items-start gap-4">
                  <div className="w-1/3">
                    <div className="text-gray-600">Network</div>
                    <div>
                      {networkInfo
                        ? `${networkId} - ${networkInfo.name}`
                        : "--"}
                    </div>
                  </div>
                  {selectedType === TYPE.YIELD_SET && (
                    <div className="flex-1">
                      <div className="text-gray-600">Yield Set</div>
                      <div>
                        {yieldsetId ? `${yieldsetId} - ${yieldsetName}` : "--"}
                      </div>
                    </div>
                  )}
                  {selectedType === TYPE.AD_UNITS && (
                    <div className="flex-1">
                      <div className="text-gray-600">
                        Ad Unit{unitInfo.length > 1 && "s"}
                        {unitInfo.length > 1 && (
                          <span> ({unitInfo.length})</span>
                        )}
                      </div>

                      {unitInfo.length === 1 ? (
                        <div>
                          {unitInfo[0].unitId} - {unitInfo[0].name}
                          <div className="text-xs leading-none text-gray-600">
                            {unitInfo[0].extGamUnitId}
                          </div>
                        </div>
                      ) : (
                        <div
                          className="rounded flex flex-wrap items-center gap-x-2 overflow-y-auto bg-gray-100 px-5 pb-8 pt-2"
                          style={{ maxHeight: "100px" }}
                        >
                          {unitInfo.map((d, i) => (
                            <React.Fragment key={d.extGamUnitId}>
                              {i > 0 && (
                                <span className="text-gray-600">/</span>
                              )}
                              <SimpleTooltip
                                key={d.extGamUnitId}
                                tooltipText={`${d.unitId} - ${d.name} (${d.extGamUnitId})`}
                                place="bottom"
                              >
                                <span className="cursor-pointer underline">
                                  {d.unitId}
                                </span>
                              </SimpleTooltip>
                            </React.Fragment>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-end gap-4">
                  <div>
                    <div className="text-gray-600">Date Range</div>
                    <DateRangePicker
                      format="YYYY-MM-DD ddd"
                      startDate={startDate}
                      endDate={endDate}
                      handleDateRangeChanged={this.handleDateRangeFilterChanged}
                    ></DateRangePicker>
                  </div>

                  <button
                    type="button"
                    className={`rounded shadow px-6 py-2 text-sm font-semibold text-white ${
                      this.checkGetData()
                        ? "cursor-not-allowed bg-blue-200 hover:bg-blue-200"
                        : "bg-blue-400 hover:bg-blue-600"
                    }`}
                    onClick={this.handleGetData}
                    disabled={this.checkGetData()}
                  >
                    Get Data
                  </button>
                </div>
              </div>
            </div>

            {errMsg && <div className="text-red-600">{errMsg}</div>}

            {parsedChartOption && (
              <div className="border-b border-t sticky top-0 z-10 border-gray-400 bg-white py-2 shadow-sm">
                <div
                  className="flex items-center justify-between"
                  style={fixedWidthStyle}
                >
                  <div className="flex items-center gap-2">
                    <SwitchTabs
                      tabs={viewOptions}
                      selectedTabKey={selectedView}
                      handleChange={(value) => {
                        this.setState({ selectedView: value });
                      }}
                    ></SwitchTabs>
                  </div>

                  <div className="flex items-center gap-2">
                    {selectedMetric === METRIC.REVENUE && (
                      <ShowInTwdCheckbox
                        isInTwd={isInTwd}
                        toggleSetTwd={() =>
                          this.setState({ isInTwd: !isInTwd })
                        }
                      ></ShowInTwdCheckbox>
                    )}
                    <SwitchTabs
                      tabs={metricOptions}
                      selectedTabKey={selectedMetric}
                      handleChange={(value) => {
                        this.setState({ selectedMetric: value });
                      }}
                    ></SwitchTabs>
                    <SimpleTooltip
                      tooltipText={`CVD Mode: ${
                        isColorBlindMode ? "ON" : "OFF"
                      }`}
                      place="left"
                    >
                      <div
                        className={`cursor-pointer ${
                          isColorBlindMode ? "text-blue-600" : "text-gray-400"
                        }`}
                        onClick={() =>
                          this.setState(
                            { isColorBlindMode: !isColorBlindMode },
                            () => {
                              this.handleChartData();
                            }
                          )
                        }
                      >
                        <FiAperture></FiAperture>
                      </div>
                    </SimpleTooltip>
                    <DemandTypeColorSetButton
                      isCVDVision={isColorBlindMode}
                      networkInfo={networkInfo}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="bg-gray-100 py-2">
              <div style={fixedWidthStyle}>
                {parsedPieChartOption && (
                  <div className="border rounded mb-2 border-gray-200 bg-white px-4 py-2">
                    <div className="font-semibold text-gray-800">
                      Daily Average by{" "}
                      {selectedView === VIEW.GROUP ? "Groups" : "Demand Type"}
                    </div>
                    <div className="flex">
                      {parsedPieChartOption.map((option, index) => (
                        <div
                          className="w-1/2"
                          key={`${selectedView}-${selectedMetric}-${isInTwd}-${isColorBlindMode}-${index}`}
                        >
                          <HighchartWrapper options={option}></HighchartWrapper>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {parsedChartOption && (
                  <div className="border rounded mb-2 border-gray-200 bg-white px-4 py-2">
                    <div className="font-semibold text-gray-800">
                      {selectedView === VIEW.GROUP ? "Group" : "Demand Type"}{" "}
                      Ratios by Date
                    </div>
                    <HighchartWrapper
                      options={parsedChartOption}
                      key={`${selectedView}-${selectedMetric}-${isInTwd}-${isColorBlindMode}`}
                    ></HighchartWrapper>
                  </div>
                )}
                {parsedLineChartOption && (
                  <div className="flex flex-col">
                    {parsedLineChartOption.map((option, index) => (
                      <HighchartWrapper
                        options={option}
                        key={`${selectedView}-${selectedMetric}-${isInTwd}-${isColorBlindMode}-${index}`}
                      ></HighchartWrapper>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }
}

export default DemandPortfolioView;
